import { useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { notifier } from 'components/Toaster';
import useGetExamBoards from 'hooks/API/GET/useGetExamBoards';
import { useManageBoard, useDeleteBoard } from 'hooks/API/POST/useManageExamInfo';
import AppBreadcrumbs from 'components/molecules/AppBreadcrumbs';
import BasicDelete from 'components/molecules/Modal/Delete/BasicDelete';
import * as AppPics from 'assets/images';

const { AppTextField, AppSelectBox, AppButton } = require('components');

const EMPTY_BOARD = { name: '', status: 'active' };

export default function ExamInfoIndex() {
  const history = useHistory();
  const { data: boardsData, isLoading } = useGetExamBoards({ enabled: true });
  const boards = boardsData?.data || [];

  const [form, setForm] = useState({ ...EMPTY_BOARD });
  const [editing, setEditing] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null, name: '' });

  const { mutate: saveBoard, isLoading: saving } = useManageBoard();
  const { mutate: deleteBoard } = useDeleteBoard();

  const handleNameChange = (e) => {
    const name = e.target.value;
    setForm((prev) => ({ ...prev, name }));
  };

  const handleEdit = (board) => {
    setEditing(board);
    setForm({ name: board.name, status: board.status });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditing(null);
    setForm({ ...EMPTY_BOARD });
  };

  const handleSubmit = () => {
    if (!form.name.trim()) {
      notifier.error('Board name is required');
      return;
    }
    saveBoard(editing ? { ...form, boardId: editing._id } : form, {
      onSuccess: () => {
        notifier.success(editing ? 'Board updated' : 'Board created');
        setEditing(null);
        setForm({ ...EMPTY_BOARD });
      },
      onError: () => notifier.error('Save failed'),
    });
  };

  const handleDeleteConfirm = () => {
    deleteBoard(deleteModal.id, {
      onSuccess: () => notifier.success('Board deleted'),
      onError: () => notifier.error('Delete failed'),
    });
    setDeleteModal({ open: false, id: null, name: '' });
  };

  return (
    <>
      <BasicDelete
        open={deleteModal.open}
        closeModal={() => setDeleteModal({ open: false, id: null, name: '' })}
        Header="Delete Board"
        Body={`Are you sure you want to delete "${deleteModal.name}" and all its exam types?`}
        handleConfirmation={handleDeleteConfirm}
      />

      <div className="mainWraper coursetabsection">
        <div className="tab">
          <ul className="tab__menu">
            <li className="back">
              <button className="btn-back" onClick={() => history.push('/podcast')}>
                <img src={AppPics.BackSvg} alt="Back" />
              </button>
            </li>
          </ul>

          <div className="tab__content ___adjust___tab___padding">
            <div className="tab__content-actHead">
              <AppBreadcrumbs
                links={[
                  { label: 'Dashboard', to: '/dashboard' },
                  { label: 'Podcast', to: '/podcast' },
                ]}
                current="Exam Info"
              />
            </div>

            <div className="tab__content-actBody">
              <div className="px-5 py-5">
                <div className="tab__contentMenu">
                  <ul className="tab__contentMenu-list">
                    <li>
                      <h2 className="prime-head p-0 boldtitle text-nowrap">Exam Info Boards</h2>
                    </li>
                  </ul>
                </div>

                {/* ── Add / Edit form ───────────────────────────────────────── */}
                <div className="tab__contentBody mb-4">
                  <div
                    style={{
                      background: '#fafafa',
                      border: '1px solid #e8e8e8',
                      borderRadius: 8,
                      padding: 20,
                      maxWidth: 600,
                    }}
                  >
                    <h4 style={{ margin: '0 0 16px', fontWeight: 600 }}>
                      {editing ? `Edit: ${editing.name}` : 'Add New Board'}
                    </h4>

                    <div className="row">
                      <div className="col-md-6">
                        <AppTextField
                          labelProps={{ label: 'Board Name *' }}
                          inputProps={{
                            placeholder: 'e.g. Kerala PSC',
                            value: form.name,
                            onChange: handleNameChange,
                          }}
                        />
                      </div>
                      <div className="col-md-6">
                        <AppSelectBox
                          labelProps={{ label: 'Status' }}
                          selectProps={{
                            value: form.status,
                            onChange: (e) => setForm({ ...form, status: e.target.value }),
                          }}
                        >
                          <option value="active">Active</option>
                          <option value="coming_soon">Coming Soon</option>
                        </AppSelectBox>
                      </div>
                    </div>

                    <div className="d-flex align-items-center mt-4" style={{ gap: 12 }}>
                      <AppButton
                        variantClass="btn-primary"
                        onClick={handleSubmit}
                        disabled={saving}
                      >
                        {saving ? 'Saving…' : editing ? 'Update Board' : 'Add Board'}
                      </AppButton>
                      {editing && (
                        <AppButton variantClass="btn-default" onClick={handleCancelEdit}>
                          Cancel
                        </AppButton>
                      )}
                    </div>
                  </div>
                </div>

                {/* ── Boards table ─────────────────────────────────────────── */}
                <div className="tab__contentBody">
                  <div className="tableWrap">
                    <div className="table-responsive">
                      <table className="table tableWrap__table">
                        <thead className="thead-light __appTheadColor">
                          <tr>
                            <th className="text-center">#</th>
                            <th>Board Name</th>
                            <th className="text-center">Status</th>
                            <th className="text-center">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {isLoading ? (
                            <tr>
                              <td colSpan={4} className="text-center py-4">
                                Loading boards…
                              </td>
                            </tr>
                          ) : boards.length === 0 ? (
                            <tr>
                              <td colSpan={4} className="text-center py-4">
                                No boards yet. Add one above.
                              </td>
                            </tr>
                          ) : (
                            boards.map((board, idx) => (
                              <tr key={board._id}>
                                <td className="text-center">{idx + 1}</td>
                                <td>{board.name}</td>
                                <td className="text-center">
                                  <span
                                    className={`badge ${
                                      board.status === 'active' ? 'badge-success' : 'badge-warning'
                                    }`}
                                    style={{ textTransform: 'capitalize' }}
                                  >
                                    {board.status.replace('_', ' ')}
                                  </span>
                                </td>
                                <td className="text-center">
                                  <Link
                                    className="actions-icons mx-2"
                                    title="View Exam Types"
                                    to={`/podcast/exam-info/${board._id}/exam-types`}
                                    style={{ display: 'inline-flex', alignItems: 'center' }}
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="18"
                                      height="18"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="#4a90d9"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      style={{ display: 'block' }}
                                    >
                                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                      <circle cx="12" cy="12" r="3" />
                                    </svg>
                                  </Link>
                                  <button
                                    className="actions-icons mx-2 text-primary"
                                    title="Edit"
                                    onClick={() => handleEdit(board)}
                                  >
                                    <img src={AppPics.EditSvg} alt="Edit" />
                                  </button>
                                  <button
                                    className="actions-icons mx-2 text-danger"
                                    title="Delete"
                                    onClick={() =>
                                      setDeleteModal({
                                        open: true,
                                        id: board._id,
                                        name: board.name,
                                      })
                                    }
                                  >
                                    <img src={AppPics.DeleteSvg} alt="Delete" />
                                  </button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
